import apiClient from './apiClient'

export interface DashboardStats {
  bots: {
    total: number
    connected: number
    disconnected: number
    authenticating: number
    failed: number
  }
  conversations: {
    total: number
    waiting_human: number
  }
  clients: {
    total: number
  }
  responseRate: number
  messages: {
    bot_today: number
    client_today: number
  }
  bots_waiting_conversations?: {
    id: number
    name: string
    status: string
    waiting_conversations: number
  }[]
}

export interface RecentActivity {
  id: number
  type: string
  message: string
  timestamp: string
  botName?: string
  clientName?: string
}

export interface BotStatus {
  id: number
  name: string
  status: 'connected' | 'authenticating' | 'disconnected' | 'failed'
  lastActivity?: string
  conversationsCount: number
}

const USER_CACHE_TTL_MS = 5000
let cachedCurrentUser: { expiresAt: number; data: any } | null = null
let currentUserRequest: Promise<any> | null = null

const getCurrentUserCached = async (): Promise<any> => {
  const now = Date.now()
  if (cachedCurrentUser && cachedCurrentUser.expiresAt > now) {
    return cachedCurrentUser.data
  }

  if (currentUserRequest) {
    return currentUserRequest
  }

  currentUserRequest = apiClient
    .get('/auth/me')
    .then((response) => {
      const userData = response.data.data
      cachedCurrentUser = {
        data: userData,
        expiresAt: Date.now() + USER_CACHE_TTL_MS
      }
      return userData
    })
    .finally(() => {
      currentUserRequest = null
    })

  return currentUserRequest
}

export const dashboardService = {
  async getStats(): Promise<{ success: boolean; data?: DashboardStats; message?: string }> {
    try {
      // Verificar se é um usuário (atendente) - usar rota de usuário diretamente
      const user = await getCurrentUserCached()
      
      if (user && user.type === 'user') {
        // Usuário (atendente) - usar rota de usuário diretamente
        const response = await apiClient.get('/user-dashboard/stats')
        return response.data
      } else {
        // Admin - usar rota de admin
        const response = await apiClient.get('/admin/stats')
        return response.data
      }
    } catch (error: any) {
      return { success: false, message: error.response?.data?.message || 'Erro ao carregar estatísticas.' }
    }
  },

  async getRecentActivities(): Promise<{ success: boolean; data?: RecentActivity[]; message?: string }> {
    try {
      // Verificar se é um usuário (atendente) - usar rota de usuário diretamente
      const user = await getCurrentUserCached()
      
      if (user && user.type === 'user') {
        // Usuário (atendente) - usar rota de usuário diretamente
        const response = await apiClient.get('/user-dashboard/activities')
        return response.data
      } else {
        // Admin - usar rota de admin
        const response = await apiClient.get('/admin/activities')
        return response.data
      }
    } catch (error: any) {
      return { success: false, message: error.response?.data?.message || 'Erro ao carregar atividades.' }
    }
  },

  async getBotsStatus(): Promise<{ success: boolean; data?: BotStatus[]; message?: string }> {
    try {
      // Verificar se é um usuário (atendente) - usar rota de usuário diretamente
      const user = await getCurrentUserCached()
      
      if (user && user.type === 'user') {
        // Usuário (atendente) - usar rota de usuário diretamente
        const response = await apiClient.get('/user-dashboard/bots')
        if (response.data.success && response.data.data) {
          // Ajustar formato dos dados para o frontend
          const bots = response.data.data.map((bot: any) => ({
            id: bot.id,
            name: bot.name,
            status: bot.status,
            lastActivity: bot.lastActivity,
            conversationsCount: bot.conversationsCount || 0
          }))
          return { success: true, data: bots }
        }
        return response.data
      } else {
        // Admin - usar rota de admin
        const response = await apiClient.get('/bots')
        if (response.data.success && response.data.data.bots) {
          const bots = response.data.data.bots.map((bot: any) => ({
            id: bot.id,
            name: bot.session_name,
            status: bot.status,
            lastActivity: bot.lastActivity || bot.updated_at,
            conversationsCount: bot.conversationsCount || 0
          }))
          return { success: true, data: bots }
        }
        return { success: false, message: 'Erro ao carregar status dos bots.' }
      }
    } catch (error: any) {
      return { success: false, message: error.response?.data?.message || 'Erro ao carregar status dos bots.' }
    }
  }
}
